import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme, Card } from '../../../components/ui';
import { auth } from '../../../config/firebase';
import { PrivateProfileService } from '../../../services/privateProfileService';
import { PrivateProfile, CoreProfile } from '../../../models/PrivateProfile';

/**
 * Manage Recommendations Section Component
 * Displays user profile information and provides access to update goals
 */
const ManageRecommendationsSection: React.FC = () => {
  const navigation = useNavigation();
  const [profile, setProfile] = useState<PrivateProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [navigating, setNavigating] = useState(false);

  useEffect(() => {
    loadProfile();
    
    // Refresh when screen comes into focus (e.g., after returning from chat)
    const unsubscribe = navigation.addListener('focus', () => {
      console.log('[ManageRecommendations] Screen focused, refreshing profile...');
      loadProfile();
    });
    
    return unsubscribe;
  }, [navigation]);

  const loadProfile = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        console.log('[ManageRecommendations] No authenticated user');
        setLoading(false);
        return;
      }

      console.log('[ManageRecommendations] Loading profile for user:', user.uid);
      const result = await PrivateProfileService.getProfile(user.uid);
      
      if (result.success && result.data) {
        console.log('[ManageRecommendations] Profile loaded successfully');
        console.log('[ManageRecommendations] CoreProfile data:', result.data.coreProfile);
        console.log('[ManageRecommendations] Profile details:', {
          hasCoreProfile: !!result.data.coreProfile,
          goals: result.data.coreProfile?.goals?.length || 0,
          interests: result.data.coreProfile?.interests?.length || 0,
          challenges: result.data.coreProfile?.challenges?.length || 0,
          traits: result.data.coreProfile?.personalityTraits?.length || 0,
        });
        setProfile(result.data);
      } else {
        console.log('[ManageRecommendations] Failed to load profile:', result.error);
      }
    } catch (error) {
      console.error('[ManageRecommendations] Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeGoals = async () => {
    if (navigating) return; // Prevent double-click
    
    setNavigating(true);
    
    try {
      const coreProfile = profile?.coreProfile;
      
      // Check if we should show the profile summary (once per day)
      const shouldShowProfileSummary = await checkShouldShowProfileSummary();
      
      if (shouldShowProfileSummary && coreProfile && profile) {
        // Build a comprehensive profile summary message
        let profileSummary = "Here's what I currently know about you:\n\n";
        
        if (coreProfile.mainFocusLabel) {
          profileSummary += "**Your Main Focus:**\n";
          profileSummary += `${coreProfile.mainFocusLabel}`;
          if (coreProfile.primaryGoalText) {
            profileSummary += ` - "${coreProfile.primaryGoalText}"`;
          }
          profileSummary += "\n\n";
        }
        
        if (coreProfile.goals && coreProfile.goals.length > 0) {
          profileSummary += "**Your Goals:**\n";
          coreProfile.goals.forEach((goal, index) => {
            profileSummary += `${index + 1}. ${goal}\n`;
          });
          profileSummary += "\n";
        }
        
        if (coreProfile.interests && coreProfile.interests.length > 0) {
          profileSummary += "**Your Interests:**\n";
          coreProfile.interests.forEach((interest, index) => {
            profileSummary += `${index + 1}. ${interest}\n`;
          });
          profileSummary += "\n";
        }
        
        if (coreProfile.challenges && coreProfile.challenges.length > 0) {
          profileSummary += "**Your Challenges:**\n";
          coreProfile.challenges.forEach((challenge, index) => {
            profileSummary += `${index + 1}. ${challenge}\n`;
          });
          profileSummary += "\n";
        }
        
        if (coreProfile.personalityTraits && coreProfile.personalityTraits.length > 0) {
          profileSummary += "**Your Personality Traits:**\n";
          profileSummary += coreProfile.personalityTraits.join(', ');
          profileSummary += "\n\n";
        }
        
        profileSummary += "What would you like me to update?";
        
        // Save this message to chat history so it appears once
        await saveProfileSummaryToChat(profileSummary);
        
        // Navigate to chat - the message will already be there
        (navigation as any).navigate('MentorChat');
      } else {
        // Just navigate to chat without adding a message
        (navigation as any).navigate('MentorChat');
      }
    } finally {
      // Reset navigating state after a delay (in case navigation is slow)
      setTimeout(() => setNavigating(false), 3000);
    }
  };
  
  const checkShouldShowProfileSummary = async (): Promise<boolean> => {
    if (!profile?.chatHistory) return true; // No chat history, show it
    
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    
    // Check if we've already shown the profile summary today
    const hasShownToday = profile.chatHistory.some((chat) => {
      const chatDate = new Date(chat.timestamp * 1000).toISOString().split('T')[0];
      return chatDate === today && chat.assistantResponse.includes("Here's what I currently know about you:");
    });
    
    return !hasShownToday;
  };
  
  const saveProfileSummaryToChat = async (profileSummary: string) => {
    const user = auth.currentUser;
    if (!user || !profile) return;
    
    const now = Date.now() / 1000;
    const chatEntry = {
      id: `profile_summary_${now}`,
      timestamp: now,
      userMessage: "I'd like to update my profile",
      assistantResponse: profileSummary,
    };
    
    try {
      await PrivateProfileService.addUserChatHistory(user.uid, chatEntry);
      console.log('[ManageRecommendations] Profile summary added to chat history');
    } catch (error) {
      console.error('[ManageRecommendations] Error saving profile summary:', error);
    }
  };

  const renderProfileSection = (
    title: string,
    items: string[] | undefined,
    icon: string
  ) => {
    if (!items || items.length === 0) return null;

    return (
      <View style={styles.profileSection}>
        <View style={styles.profileSectionHeader}>
          <Ionicons
            name={icon as any}
            size={18}
            color={Theme.colors.primary}
          />
          <Text style={styles.profileSectionTitle}>{title}</Text>
        </View>
        <View style={styles.profileItems}>
          {items.map((item, index) => (
            <View key={`${title}-${index}`} style={styles.profileItem}>
              <View style={styles.bullet} />
              <Text style={styles.profileItemText}>{item}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Card style={styles.card}>
          <ActivityIndicator size="small" color={Theme.colors.primary} />
        </Card>
      </View>
    );
  }

  const coreProfile = profile?.coreProfile;
  const hasProfileData =
    coreProfile &&
    (coreProfile.goals?.length ||
      coreProfile.interests?.length ||
      coreProfile.challenges?.length ||
      coreProfile.personalityTraits?.length);

  console.log('[ManageRecommendations] Render state:', {
    hasProfile: !!profile,
    hasCoreProfile: !!coreProfile,
    hasProfileData,
    goalsCount: coreProfile?.goals?.length || 0,
  });

  return (
    <View style={styles.container}>
      <Card style={styles.card}>
        <View style={styles.header}>
          <Ionicons
            name="settings-outline"
            size={24}
            color={Theme.colors.primary}
          />
          <Text style={styles.headerTitle}>Your Profile</Text>
        </View>

        <Text style={styles.description}>
          {hasProfileData 
            ? 'Here\'s what we know about you. Tap below to update your goals and preferences.'
            : 'Tell us about yourself to get personalized book recommendations.'}
        </Text>

        {hasProfileData && coreProfile ? (
          <View style={styles.profileContainer}>
            {renderProfileSection(
              'Goals',
              coreProfile.goals,
              'flag-outline'
            )}
            {renderProfileSection(
              'Interests',
              coreProfile.interests,
              'heart-outline'
            )}
            {renderProfileSection(
              'Challenges',
              coreProfile.challenges,
              'alert-circle-outline'
            )}
          </View>
        ) : (
          <View style={styles.emptyState}>
            <Ionicons
              name="person-add-outline"
              size={48}
              color={Theme.colors.textSecondary}
              style={styles.emptyIcon}
            />
            <Text style={styles.emptyStateText}>
              No profile information yet. Chat with your mentor to set up your personalized profile.
            </Text>
          </View>
        )}

        <Pressable
          style={[styles.button, navigating && styles.buttonDisabled]}
          onPress={handleChangeGoals}
          disabled={navigating}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="Change your goals"
          accessibilityHint="Double tap to chat with your mentor about updating your goals"
        >
          {navigating ? (
            <>
              <ActivityIndicator size="small" color="#fff" style={styles.buttonLoader} />
              <Text style={styles.buttonText}>Loading...</Text>
            </>
          ) : (
            <>
              <Text style={styles.buttonText}>
                {hasProfileData ? 'Update Your Goals' : 'Set Up Your Profile'}
              </Text>
              <Ionicons name="chatbubble-outline" size={20} color="#fff" />
            </>
          )}
        </Pressable>
      </Card>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 40,
    paddingHorizontal: Theme.spacing.lg,
  },
  card: {
    padding: Theme.spacing.lg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Theme.spacing.md,
  },
  headerTitle: {
    fontSize: 24,
    lineHeight: 32,
    fontFamily: Theme.typography.fontFamily.bold,
    color: Theme.colors.sectionHeader,
    marginLeft: Theme.spacing.sm,
  },
  description: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    lineHeight: 20,
    marginBottom: Theme.spacing.lg,
  },
  profileContainer: {
    marginBottom: Theme.spacing.lg,
  },
  profileSection: {
    marginBottom: Theme.spacing.md,
  },
  profileSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Theme.spacing.sm,
  },
  profileSectionTitle: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    marginLeft: Theme.spacing.xs,
  },
  profileItems: {
    marginLeft: Theme.spacing.lg,
  },
  profileItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: Theme.spacing.xs,
  },
  bullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: Theme.colors.textSecondary,
    marginTop: 8,
    marginRight: Theme.spacing.sm,
  },
  profileItemText: {
    flex: 1,
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
    lineHeight: 20,
  },
  emptyState: {
    padding: Theme.spacing.xl,
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.md,
    marginBottom: Theme.spacing.lg,
    alignItems: 'center',
  },
  emptyIcon: {
    marginBottom: Theme.spacing.md,
    opacity: 0.5,
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Theme.colors.primary,
    paddingVertical: Theme.spacing.md,
    paddingHorizontal: Theme.spacing.lg,
    borderRadius: Theme.borderRadius.md,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  buttonLoader: {
    marginRight: Theme.spacing.sm,
  },
  buttonText: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: '#fff',
    marginRight: Theme.spacing.sm,
  },
});

export default ManageRecommendationsSection;
